import React, { useEffect, useState } from 'react';
import { Row, Col } from 'reactstrap';
import DOMPurify from 'dompurify';
import styled, { keyframes } from 'styled-components';

import Feedback from './Feedback';
import LoadingMessage from './LoadingMessage';
import PdfViewer from './PdfViewer';
import { getMessageCitations, resolveCitationLinks, addCitationTooltips, citationHeaderSubtitle } from './citations';
import { useCitationViewer } from 'hooks/useCitationViewer';
import whitelogoicon from 'assets/img/bevan-icon.png';
import AnimatedGradientText from 'components/styles/AnimatedGradientText';

// Shared with the argon-dashboard theme (--bs-dark / --bs-heading-color and the
// citation-blue gradient used elsewhere in the app) - kept as local constants
// since these styled-components can't reach into the CSS custom properties.
const COLOR_TEXT_DARK = '#344767';
const COLOR_TEXT_MUTED = '#596c8f';
const COLOR_CITATION_BLUE = '#2152ff';
const COLOR_CITATION_BLUE_HOVER = '#0f3ad1';
const COLOR_ERROR = '#e63757';
const COLOR_ICON_MUTED = '#8898aa';

const blink = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
`;

const StreamingCursor = styled.span`
    display: inline-block;
    width: 0.5em;
    height: 1em;
    margin-left: 2px;
    background-color: currentColor;
    vertical-align: text-bottom;
    animation: ${blink} 1s step-start infinite;
`;

const toastIn = keyframes`
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
`;

const CitationErrorToast = styled.div`
    position: fixed;
    bottom: 1.5rem;
    right: 1.5rem;
    z-index: 1080;
    display: flex;
    align-items: flex-start;
    gap: 0.65rem;
    max-width: 340px;
    padding: 0.85rem 1rem;
    background: #ffffff;
    border-radius: 12px;
    border-left: 4px solid ${COLOR_ERROR};
    box-shadow: 0 10px 30px rgba(20, 20, 43, 0.14);
    animation: ${toastIn} 0.25s ease-out;

    .citation-toast-icon {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: rgba(230, 55, 87, 0.1);
        color: ${COLOR_ERROR};
        font-size: 0.85rem;
    }

    .citation-toast-body {
        flex: 1;
        font-size: 0.85rem;
        line-height: 1.4;
        color: ${COLOR_TEXT_DARK};
        padding-top: 2px;
    }

    .citation-toast-close {
        flex-shrink: 0;
        background: none;
        border: none;
        color: ${COLOR_ICON_MUTED};
        font-size: 0.9rem;
        line-height: 1;
        padding: 2px;
        cursor: pointer;
    }

    .citation-toast-close:hover {
        color: ${COLOR_TEXT_DARK};
    }
`;

const CitationContext = styled.section`
    padding: 0 0 1rem;
    color: ${COLOR_TEXT_DARK};

    .citation-section {
        margin-bottom: 0.35rem;
        color: ${COLOR_TEXT_MUTED};
        font-size: 0.9rem;
    }

    blockquote {
        margin: 0.5rem 0 0;
        padding-left: 1rem;
        border-left: 3px solid ${COLOR_CITATION_BLUE};
        font-size: 0.95rem;
        line-height: 1.5;
    }
`;

// The message body is injected via dangerouslySetInnerHTML, so citation links
// can't be styled with a scoped className prop - style them here instead. The
// theme's default link/body colour (rgb(52, 71, 103)) is nearly indistinguishable
// from body text, so citations need an explicit, clearly-a-link colour.
const MessageBody = styled.div`
    .citation-link {
        color: ${COLOR_CITATION_BLUE};
        text-decoration: underline;
        text-decoration-style: dotted;
        text-underline-offset: 2px;
        cursor: pointer;
    }

    .citation-link:hover {
        color: ${COLOR_CITATION_BLUE_HOVER};
        text-decoration-style: solid;
    }

    .citation-marker {
        display: inline-block;
        margin-left: 1px;
        padding: 0 4px;
        font-size: 0.7em;
        font-weight: 600;
        line-height: 1.5;
        color: ${COLOR_CITATION_BLUE};
        background: rgba(33, 82, 255, 0.1);
        border-radius: 4px;
        cursor: pointer;
    }

    .citation-marker:hover {
        color: #ffffff;
        background: ${COLOR_CITATION_BLUE};
    }
`;

const ChatMessageDisplay = ({
    mapping, scrollRef, sessionId, regenerateResponse, regMsdId, isLoading, totalMessages
}) => {
    const [visibleFeedbackSet, setVisibleFeedbackSet] = useState(new Set());
    const messages = Object.values(mapping);
    const {
        viewer, error: citationError, openCitation, closeViewer, dismissError, handleLoadError,
    } = useCitationViewer();

    useEffect(() => {
        if (!citationError) return;
        const timer = setTimeout(dismissError, 6000);
        return () => clearTimeout(timer);
    }, [citationError, dismissError]);

    const handleMouseOver = (id) => {
        setVisibleFeedbackSet(new Set(visibleFeedbackSet.add(id)));
    };

    const handleMouseOut = (id) => {
        visibleFeedbackSet.delete(id);
        setVisibleFeedbackSet(new Set(visibleFeedbackSet));
    };

    // Inline citation links are injected as plain HTML (dangerouslySetInnerHTML),
    // so a single delegated click handler per message intercepts clicks on the
    // data-citation-index markers resolveCitationLinks() left behind, rather than
    // relying on real navigable hrefs (the model never emits a real URL).
    const handleCitationClick = (event, citations) => {
        const target = event.target.closest('[data-citation-index]');
        if (!target) return;
        event.preventDefault();
        const citationIndex = Number(target.dataset.citationIndex);
        const citation = citations.find(c => c.index === citationIndex);
        if (citation) openCitation(citation);
    };

    return (
        <>
        <div className="h-100 pt-4" ref={scrollRef}>
            {messages.map((entry, index) => {
                const content = entry.message.content.parts.join(" ");
                const hasContent = content && content.length > 0;
                const isRegenerating = regMsdId === entry.id;
                const isLastMessage = index === messages.length - 1;
                const isSystemProcessing = isLoading && isLastMessage;
                const citations = getMessageCitations(entry.message);

                return (
                    <Row
                        key={entry.id}
                        className="py-3 justify-content-center"
                        onMouseOver={() => handleMouseOver(entry.id)}
                        onMouseOut={() => handleMouseOut(entry.id)}
                    >
                        <Col xl="9">
                            <div className="d-flex">
                                <div className="me-3">
                                    {entry.message.author.role === 'user' ?
                                        <div className="icon icon-sm icon-shape message-avatar message-avatar-user text-center rounded-circle">
                                            <i className="fa fa-user text-sm opacity-10 text-white" aria-hidden="true"></i>
                                        </div>
                                        :
                                        <div className="icon icon-sm icon-shape message-avatar message-avatar-assistant text-center rounded-circle">
                                            <img src={whitelogoicon} className="w-90 px-1 py-1" alt="" />

                                        </div>
                                    }
                                </div>
                                <div className='w-100'>
                                    <div className="justify-content-between align-items-center">
                                        <h5 className="text-black mb-0 fs-6">
                                            {entry.message.author.role === 'user' ? 'You' : (
                                                (totalMessages === index + 1 && isLoading) ? (
                                                    <AnimatedGradientText text="Bevan" />
                                                ) : 'Bevan'
                                            )}
                                        </h5>
                                    </div>
                                    <div className='w-100'>
                                        {(() => {
                                            if (hasContent) {
                                                const formattedHtml = addCitationTooltips(
                                                    resolveCitationLinks(
                                                        content.replace(/```html|```/g, '').trim()
                                                            .replace(/<h1(?=[^>]*>)/g, '<h1 class="h3 text-black"')
                                                            .replace(/<h2(?=[^>]*>)/g, '<h2 class="h4 text-black"')
                                                            .replace(/<h3(?=[^>]*>)/g, '<h3 class="h5 text-black"')
                                                            .replace(/<h4(?=[^>]*>)/g, '<h4 class="h6 text-black"')
                                                            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                                    ),
                                                    citations
                                                );

                                                return (
                                                    <MessageBody className="text-message mb-1 text-black"
                                                        data-message-author-role={entry.message.author.role}
                                                        data-message-id={entry.message.id}
                                                        data-parent-id={entry.parent}
                                                        onClick={(event) => handleCitationClick(event, citations)}
                                                    >
                                                        <span
                                                            dangerouslySetInnerHTML={{
                                                                __html: DOMPurify.sanitize(formattedHtml)
                                                            }}
                                                        />
                                                        {isSystemProcessing && <StreamingCursor />}
                                                    </MessageBody>
                                                );
                                            } else if (isRegenerating || isSystemProcessing) {
                                                // Show appropriate loading state
                                                return <LoadingMessage />;
                                            } else {
                                                // Only show "No response available" for truly empty/failed responses
                                                return (
                                                    <div className="text-message mb-1 text-muted">
                                                        <em>No response available</em>
                                                    </div>
                                                );
                                            }
                                        })()}

                                        {entry.message.author.role === 'assistant' && hasContent ?
                                            <Feedback
                                                isVisible={visibleFeedbackSet.has(entry.id)}
                                                isLastMessage={index === messages.length - 1}
                                                messageId={entry.message.id}
                                                parentId={entry.parent}
                                                sessionId={sessionId}
                                                regenerateResponse={regenerateResponse}
                                            /> : ''}
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                );
            })}
        </div>

        <div
            className={`offcanvas offcanvas-end w-60 ${viewer ? 'show' : ''}`}
            tabIndex="-1"
            style={{ visibility: viewer ? 'visible' : 'hidden' }}
        >
            <div className="offcanvas-header">
                <div>
                    <h5 className="mb-0">{viewer?.source || 'PDF Viewer'}</h5>
                    {viewer && citationHeaderSubtitle(viewer) && (
                        <div className="text-muted small">{citationHeaderSubtitle(viewer)}</div>
                    )}
                </div>
                <button type="button" className="btn-close text-reset" aria-label="Close" onClick={closeViewer}></button>
            </div>
            <div className="offcanvas-body">
                {viewer && (
                    <>
                        {/* With regions, the cited passage is highlighted directly on the
                            rendered page (see PdfViewer) - the quote block is only needed
                            as a fallback for documents ingested before per-element region
                            coordinates existed, or sources with no coordinates at all. */}
                        {!viewer.regions?.length && viewer.quote && (
                            <CitationContext>
                                {viewer.section && <div className="citation-section">{viewer.section}</div>}
                                <div className="text-muted small">Page {viewer.page}</div>
                                <blockquote>{viewer.quote}</blockquote>
                            </CitationContext>
                        )}
                        <PdfViewer
                            pdf={viewer.pdfUrl}
                            initialPage={viewer.page}
                            regions={viewer.regions}
                            isOpen
                            onClose={closeViewer}
                            onLoadError={handleLoadError}
                        />
                    </>
                )}
            </div>
        </div>
        {viewer && <div className="offcanvas-backdrop fade show" onClick={closeViewer}></div>}

        {citationError && (
            <CitationErrorToast role="alert">
                <span className="citation-toast-icon">
                    <i className="fa fa-exclamation" aria-hidden="true"></i>
                </span>
                <span className="citation-toast-body">{citationError}</span>
                <button
                    type="button"
                    className="citation-toast-close"
                    aria-label="Dismiss"
                    onClick={dismissError}
                >
                    <i className="fa fa-times" aria-hidden="true"></i>
                </button>
            </CitationErrorToast>
        )}
        </>
    );
};

export default ChatMessageDisplay;
